import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomPaginator, GenericTable, GenericTableProps } from '@cdaas/reusable-components-daas';
import { PageInfo, UserGroupResponse, UserListUser } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { Gutter, Title } from '@gaiads/telia-react-component-library';

import { assetAdminUserClient } from '../../../api-client/asset-admin-client';

interface UserGroupUsersParam {
  id: string;
  usergroupid: string;
}

interface UserGroupUsersProps {
  userGroup: UserGroupResponse;
}

const UserGroupUsers: FunctionComponent<UserGroupUsersProps> = ({ userGroup }) => {
  const [loading, setLoading] = useState<GenericTableProps<UserListUser>['loading']>('short');
  const [apiError, setApiError] = useState(false);
  const [users, setUsers] = useState<UserListUser[] | undefined>();
  const [usersPageInfo, setUsersPageInfo] = useState<PageInfo | undefined>();
  const { t } = useTranslation('userGroups');

  const history = useHistory();
  const organizationId = useParams<UserGroupUsersParam>().id;

  const pageSize = 10;

  const fetchUserGroupUsers = async (userGroupId: string, organizationId: string, pageSize: number, page: number) => {
    setLoading('short');
    try {
      const userGroupUsers = (await assetAdminUserClient.adminUserList(organizationId, userGroupId, pageSize, page)).data;

      if (userGroupUsers.contents.length) {
        setUsers(userGroupUsers.contents);
      }
      if (userGroupUsers.page_metadata) {
        setUsersPageInfo(userGroupUsers.page_metadata);
      }
    } catch (error) {
      console.error(error);
      setApiError(true);
    }
    setLoading('none');
  };

  useEffect(() => {
    fetchUserGroupUsers(userGroup.id!, organizationId, pageSize, 0);
  }, [userGroup, organizationId]);

  const handleRowClick = (row: UserListUser) => {
    history.push(`${history.location.pathname}/users/${row.id}`, { clickedFromPathname: history.location.pathname, clickedFromPage: 'usergroups' });
  };

  return (
    <>
      <Title size={'md'}>{t('userGroupUsers.title')}</Title>
      <Gutter />
      <div className={'user-group-users-table'}>
        <GenericTable
          loading={loading}
          errorText={apiError ? t('userGroupUsers.usersTable.apiError') : undefined}
          noDataText={t('userGroupUsers.usersTable.noDataText')}
          inputData={users}
          rowConfigs={[
            {
              header: t('userGroupUsers.usersTable.nameHeader'),
              formatValue: (_keyValue, row) =>
                row.given_name && row.family_name ? `${row.given_name} ${row.family_name}` : t('userGroupUsers.usersTable.inactiveUser').toString(),
            },
            {
              header: t('userGroupUsers.usersTable.phoneHeader'),
              key: 'phone_number',
            },
            {
              header: t('userGroupUsers.usersTable.emailHeader'),
              key: 'email',
            },
          ]}
          buttonConfig={{
            name: t('UserGroups.userGroupsTable.buttonText'),
            buttonClickCallback: row => handleRowClick(row),
            buttonProps: {
              iconOnRight: true,
              iconName: 'chevron-right',
              variant: 'secondary',
            },
          }}
        />
        <CustomPaginator
          activatePageInfoObject={usersPageInfo}
          onPageChange={pageChangeInfo => fetchUserGroupUsers(userGroup.id!, organizationId, pageSize, pageChangeInfo.nextChosenPageNumber)}
          paginatorStyleProps={{
            pageSelectorProps: { centered: false },
          }}
        />
      </div>
    </>
  );
};

export default UserGroupUsers;
